import { useCallback, useEffect, useRef, useState } from 'react';
import './OzonAutomationTactic.css';
import { fmtDateTime } from '../../../../utils/ruDates';
import { MetricChartPanel, MetricHalves, rolling } from './OzonMetricPanels';

const number = value => value == null ? '—' : Number(value).toLocaleString('ru-RU', { maximumFractionDigits: 2 });
const signed = value => (value == null ? '—' : `${value > 0 ? '+' : ''}${(value * 100).toFixed(0)}%`);
const stamp = iso => fmtDateTime(iso);
const hhmm = minute => `${String(Math.floor(minute / 60)).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`;
const failure = err => typeof err?.detail === 'string' ? err.detail : err?.detail?.message || err?.message || 'Не удалось выполнить';

const SOURCE_CLASS = { TACTIC: 'is-tactic', CALIBRATION: 'is-calibration', STOCK: 'is-stock', STRATEGY: 'is-strategy', MANUAL: 'is-manual', HYPOTHESIS: 'is-hypothesis' };
const STATUS_CLASS = { SENT: 'is-ok', WOULD: 'is-optional', SKIPPED: 'is-waiting', ERROR: 'is-action' };

function Help({ text }) {
  return <i className='oz-auto__help' title={text} aria-label={text}>?</i>;
}

// ── Калибровка ──────────────────────────────────────────────────────────────

export function CalibrationCard({ calibration, capacity, busy, mode, status, onStart, onStop }) {
  const [confirm, setConfirm] = useState(false);
  const running = calibration?.status === 'RUNNING';
  const measured = capacity?.bid?.measured;
  const blocked = status !== 'ACTIVE' ? 'Автоматизация на паузе'
    : mode === 'OBSERVE' ? 'В режиме «Наблюдение» калибровка не запускается: она меняет ставки и цены' : '';
  return <div className={`oz-tactic__calibration ${running ? 'is-running' : ''}`}>
    <div className='oz-tactic__calibration-head'>
      <h4>Калибровка группы
        <Help text='Три дня ставка каждый час стоит на одном из трёх уровней — база, +15%, −15%, а цена каждые 4 часа — база, −2%, +2%. За три дня каждый час видит каждый уровень ровно один раз, каждый день содержит все уровни поровну. Сравнение идёт с теми же днями недели до калибровки. Так измеряется, сколько кликов добавляет ставка в каждый час и как цена меняет переход в корзину. Дальше оценка уточняется каждый день по всей истории — калибровка лишь даёт ей точку опоры.' /></h4>
      {running && <span className='oz-tactic__badge is-calibration'>идёт</span>}
      {calibration?.status === 'DONE' && <span className='oz-tactic__badge is-ok'>завершена</span>}
      {calibration?.status === 'STOPPED' && <span className='oz-tactic__badge is-waiting'>остановлена</span>}
    </div>
    {running ? <>
      <div className='oz-tactic__progress'><span style={{ width: `${Math.round((calibration.progress || 0) * 100)}%` }} /></div>
      <p>С {stamp(calibration.started_at)} до {stamp(calibration.ends_at)} · пройдено {Math.round((calibration.progress || 0) * 100)}%.
        Сейчас ставка: <strong>{calibration.bid_level || '—'}</strong>, цена: <strong>{calibration.price_level || '—'}</strong>.
        Тактика на это время не вмешивается.</p>
      {calibration.frozen && <p className='oz-auto__hint is-action'>{calibration.frozen}. Уровни не переключаются,
        часы калибровки стоят — время добавится, когда статистика пойдёт.</p>}
      {calibration.paused_minutes > 0 && <p className='oz-auto__muted'>Расписание сдвинуто на
        {' '}{Math.round(calibration.paused_minutes / 60 * 10) / 10} ч: столько калибровка не могла мерить.</p>}
      <button type='button' disabled={busy} onClick={onStop}>Остановить и вернуть исходные ставки и цены</button>
    </> : <>
      <p>{calibration?.status === 'DONE'
        ? `Проведена ${stamp(calibration.started_at)} — ${stamp(calibration.finished_at)}. Ёмкость реакции ниже уточняется каждый день.`
        : measured
          ? 'Реакция группы на ставку уже измерена по накопленной истории. Калибровку можно провести, чтобы уточнить её по всем часам.'
          : 'Реакция группы на ставку по часам ещё не измерена: тактика шагает по оценке «клики следуют за ставкой». Три дня калибровки дадут измерение по каждому часу.'}</p>
      {!confirm && <button type='button' className='oz-auto__primary' disabled={busy || Boolean(blocked)}
        title={blocked} onClick={() => setConfirm(true)}>
        {calibration?.status ? 'Запустить калибровку заново (3 дня)' : 'Запустить калибровку (3 дня)'}</button>}
      {blocked && <p className='oz-auto__muted'>{blocked}.</p>}
      {confirm && <div className='oz-auto__experiment'>
        <p><strong>Три дня система будет менять ставки и цены по сетке.</strong> Ставка — ±15% от текущей,
          не выше максимальной и не ниже минимума OZON; без заданной максимальной ставки вверх она не пойдёт.
          Цена — ±2%, внутри коридора цен; без заданной границы цена в эту сторону не двигается.
          Если задан бюджет проверок и он исчерпан, ставка выше базы не поднимается.
          По окончании ставки и цены вернутся к сегодняшним.</p>
        <button type='button' className='oz-auto__primary' disabled={busy}
          onClick={async () => { await onStart(); setConfirm(false); }}>Да, запустить</button>
        <button type='button' disabled={busy} onClick={() => setConfirm(false)}>Отмена</button>
      </div>}
    </>}
  </div>;
}

// ── График дня ──────────────────────────────────────────────────────────────

const W = 960, LEFT = 55, RIGHT = 55;

// «Динамика группы»: итог каждого дня (или недели) в двух половинах одного графика. Сверху финансы (выручка, прибыль,
// рентабельность, ДРР общий), снизу реклама (расход, клики, корзины, заказы с рекламы, ДРР рекламный, ставка). Период
// по умолчанию 14 дней, можно выбрать другой или задать свой; группировка по дням или неделям.
const DYNAMICS_PERIODS = [[7, '7 дней'], [14, '14 дней'], [30, '30 дней'], [60, '60 дней'], [90, '90 дней']];
const SUMMED = ['orders', 'ad_orders', 'revenue', 'profit', 'carts', 'clicks', 'ad_spend', 'all_ad_spend'];
const AVERAGED = ['drr_ad', 'drr_total', 'margin_pct', 'bid_avg', 'price_avg', 'shelf_avg'];
const dayMonth = iso => iso.split('-').reverse().slice(0, 2).join('.');
const shiftIso = (iso, days) => {
  const date = new Date(`${iso}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};
const mondayOf = iso => shiftIso(iso, -((new Date(`${iso}T00:00:00Z`).getUTCDay() + 6) % 7));

// Дни → точки графика: по дням как есть (со скользящим средним за 7 дней), по неделям — суммы и средние.
export function dynamicsRows(days, mode) {
  const sorted = [...days].sort((x, y) => x.date.localeCompare(y.date));
  if (mode === 'day') {
    const rows = sorted.map(day => ({ ...day, id: day.date, label: day.date }));
    ['clicks', 'carts'].forEach(key => rolling(rows.map(row => row[key]), 7).forEach((value, index) => {
      rows[index][`${key}_avg7`] = value == null ? null : Math.round(value * 10) / 10;
    }));
    return rows;
  }
  const weeks = new Map();
  sorted.forEach(day => {
    const start = mondayOf(day.date);
    weeks.set(start, [...(weeks.get(start) || []), day]);
  });
  return [...weeks.entries()].map(([start, members]) => {
    const row = { id: start, label: `${dayMonth(start)}–${dayMonth(shiftIso(start, 6))}` };
    SUMMED.forEach(key => {
      const values = members.map(day => day[key]).filter(value => value != null);
      row[key] = values.length ? Math.round(values.reduce((sum, value) => sum + value, 0) * 100) / 100 : null;
    });
    AVERAGED.forEach(key => {
      const values = members.map(day => day[key]).filter(value => value != null);
      row[key] = values.length ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length * 100) / 100 : null;
    });
    return row;
  });
}

const DYNAMICS_HALVES = [
  { key: 'finance', title: 'Финансы', series: [
    { key: 'revenue', label: 'Выручка (сумма заказов)', unit: '₽', color: '#2563eb', side: 'left' },
    { key: 'profit', label: 'Чистая прибыль', unit: '₽', color: '#0f766e', side: 'left' },
    { key: 'orders', label: 'Заказы', unit: 'шт.', color: '#16a34a' },
    { key: 'margin_pct', label: 'Рентабельность', unit: '%', color: '#9333ea', side: 'right' },
    { key: 'drr_total', label: 'ДРР общий', unit: '%', color: '#dc2626', side: 'right' },
    { key: 'price_avg', label: 'Цена продавца', unit: '₽', color: '#0891b2', group: 'price' },
    { key: 'shelf_avg', label: 'Витрина', unit: '₽', color: '#0891b2', group: 'price', dashed: '6 3' },
  ] },
  { key: 'ads', title: 'Реклама', series: [
    { key: 'ad_spend', label: 'Расход РК', unit: '₽', color: '#ea580c', side: 'left' },
    { key: 'all_ad_spend', label: 'Вся реклама', unit: '₽', color: '#b45309', side: 'left' },
    { key: 'bid_avg', label: 'Средняя ставка', unit: '₽', color: '#7c3aed' },
    { key: 'clicks', label: 'Клики РК', unit: 'шт.', color: '#2563eb', side: 'right' },
    { key: 'clicks_avg7', label: 'Клики, среднее 7 дн.', unit: 'шт.', color: '#2563eb', group: 'clicks', dashed: '5 4' },
    { key: 'carts', label: 'Корзины РК', unit: 'шт.', color: '#f59e0b', side: 'right' },
    { key: 'carts_avg7', label: 'Корзины, среднее 7 дн.', unit: 'шт.', color: '#f59e0b', group: 'carts', dashed: '5 4' },
    { key: 'ad_orders', label: 'Заказы с рекламы', unit: 'шт.', color: '#16a34a' },
    { key: 'drr_ad', label: 'ДРР рекламный', unit: '%', color: '#ef4444' },
  ] },
];

export function GroupDynamics({ base, request }) {
  const [preset, setPreset] = useState('14');
  const [range, setRange] = useState({ from: '', to: '' });
  const [mode, setMode] = useState('day');
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const requestRef = useRef(request);
  requestRef.current = request;
  const query = preset === 'custom'
    ? (range.from && range.to && range.from <= range.to ? `date_from=${range.from}&date_to=${range.to}` : null)
    : `days=${preset}`;
  useEffect(() => {
    if (!query) return undefined;
    let alive = true;
    setLoading(true);
    requestRef.current(`${base}/stats-series?${query}`).then(result => {
      if (!alive) return;
      const history = Object.fromEntries((result.history || []).map(item => [item.date, item]));
      setDays((result.series || []).map(day => ({
        ...day, bid_avg: history[day.date]?.bid_avg ?? null, price_avg: history[day.date]?.price_avg ?? null,
        shelf_avg: history[day.date]?.shelf_avg ?? null,
      })));
      setError('');
    }).catch(err => { if (alive) setError(failure(err)); }).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [base, query]);
  const rows = dynamicsRows(days, mode);
  return <div className='oz-auto__planner'>
    <div className='oz-tactic__head'>
      <h4>Динамика группы<Help text='Итог каждого завершённого дня по группе. Сверху финансы (по «Экономике»: выручка, прибыль, рентабельность, ДРР общий), снизу реклама выбранных РК (расход, клики, корзины, заказы с рекламы, ДРР рекламный) и средняя ставка из срезов тактики. По неделям: суммы по дням, а проценты, ставка и цена — среднее по дням. У каждого показателя своя шкала; цветные шкалы по краям — у первого отмеченного показателя слева и справа.' /></h4>
    </div>
    <div className='oz-auto__chart-controls'>
      <label>Период <select value={preset} onChange={event => setPreset(event.target.value)}>
        {DYNAMICS_PERIODS.map(([value, label]) => <option key={value} value={String(value)}>{label}</option>)}
        <option value='custom'>Свой период</option></select></label>
      {preset === 'custom' && <>
        <label>с <input type='date' aria-label='Начало периода' value={range.from} onChange={event => setRange({ ...range, from: event.target.value })} /></label>
        <label>по <input type='date' aria-label='Конец периода' value={range.to} onChange={event => setRange({ ...range, to: event.target.value })} /></label>
      </>}
      <label>Группировка <select value={mode} onChange={event => setMode(event.target.value)}>
        <option value='day'>по дням</option><option value='week'>по неделям</option></select></label>
      {loading && <span className='oz-auto__muted'>Загрузка…</span>}
    </div>
    {error && <p role='alert' className='bidder__error'>{error}</p>}
    {!query && <p className='oz-auto__muted'>Укажите начало и конец периода.</p>}
    {query && !loading && rows.length === 0 && !error && <p className='oz-auto__muted'>За период нет завершённых дней с данными.</p>}
    {rows.length > 0 && <MetricHalves id='group-dynamics' points={rows} halves={DYNAMICS_HALVES}
      defaultOn={['revenue', 'profit', 'drr_total', 'ad_spend', 'clicks', 'ad_orders', 'drr_ad']}
      footer={mode === 'week' ? 'По неделям: суммы по дням недели (понедельник — воскресенье), проценты, ставка и цена — среднее по дням. Крайние недели периода могут быть неполными.' : null} />}
  </div>;
}

// «День группы по получасам»: один график. Клики, «обычно к этому часу» и цель делят шкалу — виден темп против плана;
// заказы, прибыль, ДРР, ставка и цена — те же получасы на той же оси. Поправки тактики — треугольники внизу.
export function TacticDayChart({ day, sources, totalDrr }) {
  if (!day) return null;
  const byMinute = Object.fromEntries((day.points || []).map(point => [point.minute, point]));
  const usualAt = Object.fromEntries((day.usual || []).map(item => [item.minute, item.value]));
  const points = [];
  for (let minute = 0; minute <= 1440; minute += 30) {
    const fact = byMinute[minute] || {};
    const usual = usualAt[minute] ?? null;
    points.push({
      id: minute, label: hhmm(minute), at: minute / 1440,
      clicks: fact.clicks ?? null, carts: fact.carts ?? null, orders: fact.orders ?? null,
      profit: fact.profit ?? null, drr_ad: fact.drr_ad ?? null, bid: fact.bid ?? null,
      price: fact.price ?? null, shelf: fact.shelf ?? null,
      usual, target: usual == null ? null : Math.round(usual * day.target_factor * 10) / 10,
    });
  }
  const now = day.is_today ? new Date() : null;
  const nowAt = now ? (now.getHours() * 60 + now.getMinutes()) / 1440 : null;
  const marks = (day.marks || []).map(mark => ({
    at: (mark.minute + 15) / 1440, up: mark.up >= mark.down,
    className: `oz-tactic__mark ${SOURCE_CLASS[mark.source] || ''} ${mark.status === 'WOULD' ? 'is-would' : ''} ${mark.status === 'ERROR' ? 'is-error' : ''}`,
    title: `${hhmm(mark.minute)}–${hhmm(mark.minute + 30)} · ${sources?.[mark.source] || mark.source} · ${mark.lever === 'PRICE' ? 'цена' : mark.lever === 'GOAL' ? 'цель' : 'ставка'}: ${mark.count} (${mark.up} вверх, ${mark.down} вниз)${mark.status === 'WOULD' ? ' — «сделал бы»' : ''}`,
  }));
  const footer = [
    points.some(point => point.clicks != null) ? '' : 'За этот день срезов нет: они снимаются каждые полчаса, пока автоматизация активна.',
    !day.usual && day.points?.length ? 'Обычный уровень появится, когда накопится 3 дня срезов.' : '',
    '▲▼ — поправки тактики (синие), калибровки и ручные; бледные — «сделал бы».',
    day.estimate_note || '',
    totalDrr ? `ДРР общий (${totalDrr.date}, последний закрытый день): ${number(totalDrr.value)}% — приходит по итогам дня.` : '',
  ].filter(Boolean).join(' ');
  return <MetricChartPanel id='tactic-day' points={points} marks={marks} nowAt={nowAt} footer={footer}
    left={LEFT} right={RIGHT} width={W}
    ticks={[0, 360, 720, 1080, 1440].map(minute => ({ at: minute / 1440, label: hhmm(minute) }))}
    defaultOn={['clicks', 'usual', 'target', 'orders', 'drr_ad', 'bid']}
    series={[
      { key: 'clicks', label: 'Клики группы', unit: 'шт.', color: '#2563eb', group: 'clicks' },
      { key: 'usual', label: `Обычно к этому часу${day.usual_days ? ` (${day.usual_days} дн.)` : ''}`, unit: 'шт.', color: '#64748b', group: 'clicks', dashed: '5 4' },
      { key: 'target', label: `Цель (×${number(day.target_factor)})`, unit: 'шт.', color: '#65a30d', group: 'clicks', dashed: '2 3' },
      { key: 'carts', label: 'Корзины', unit: 'шт.', color: '#f59e0b' },
      { key: 'orders', label: 'Заказы с рекламы', unit: 'шт.', color: '#16a34a' },
      { key: 'profit', label: 'Чистая прибыль (оценка)', unit: '₽', color: '#0f766e' },
      { key: 'drr_ad', label: 'ДРР рекламный (оценка)', unit: '%', color: '#ea580c' },
      { key: 'bid', label: 'Средняя ставка', unit: '₽', color: '#7c3aed' },
      { key: 'price', label: 'Цена продавца', unit: '₽', color: '#0891b2', group: 'price' },
      { key: 'shelf', label: 'Витрина (замер)', unit: '₽', color: '#0891b2', group: 'price', dashed: '6 3' },
    ]} />;
}

// ── Решение по группе ───────────────────────────────────────────────────────

export function GroupDecision({ tactics, mode }) {
  const group = tactics?.group;
  if (!group) return null;
  return <div className='oz-tactic__decision'>
    <div className='oz-tactic__kpis'>
      <span><small>Окно</small><strong>{group.from}–{group.to}</strong></span>
      <span><small>Кликов за окно</small><strong>{number(group.fact)}</strong></span>
      <span><small>Обычно за эти часы</small><strong>{number(group.usual)}</strong></span>
      <span><small>Цель</small><strong>{number(group.target)}</strong></span>
      <span className={group.deviation == null ? '' : group.deviation < -0.07 ? 'is-behind' : group.deviation > 0.07 ? 'is-ahead' : 'is-ok'}>
        <small>Отклонение</small><strong>{signed(group.deviation)}</strong></span>
      <span title={group.elasticity_measured ? 'Измерено по истории срезов' : 'Ещё не измерено — используется оценка'}>
        <small>Отклик на ставку в этот час</small><strong>{number(group.elasticity)}{group.elasticity_measured ? '' : ' (оценка)'}</strong></span>
    </div>
    <p><strong>Решение:</strong> {group.step != null ? `ставка группы ${group.step > 0 ? '+' : ''}${(group.step * 100).toFixed(1)}% — ` : ''}{group.reason || '—'}</p>
    <p className='oz-auto__muted'>Сравнивается то, что группа набрала за последние два часа, с тем, что эти же часы
      обычно приносят ({group.source}); поправка на день недели ×{number(group.weekday_factor)}, цель ×{number(group.factor)}.
      Обе стороны измерены одинаково, поэтому задержка статистики OZON не выглядит как отставание.</p>
    {mode !== 'AUTO' && <p className='oz-auto__muted'>Режим не «Автоматический»: поправки не отправляются,
      в журнал пишется, что тактика сделала бы.</p>}
    {(tactics.notes || []).map(note => <p key={note} className='oz-auto__hint is-action'>{note}</p>)}
  </div>;
}

// ── Ёмкость реакции ─────────────────────────────────────────────────────────

export function CapacityCard({ capacity }) {
  if (!capacity?.bid) return null;
  const blocks = capacity.bid.blocks || [];
  const max = Math.max(1.5, ...blocks.map(block => block.elasticity));
  const prices = Object.entries(capacity.price || {});
  return <div className='oz-tactic__capacity'>
    <h4>Ёмкость реакции
      <Help text='Сколько процентов кликов даёт +1% ставки в каждый блок часов: 1 — клики растут вровень со ставкой, 0,2 — ставка почти ничего не добавляет. Считается по всем срезам за 30 дней: день и час сами по себе вычитаются, остаётся вклад ставки. Пока ставка почти не менялась — это оценка, а не измерение.' /></h4>
    <div className='oz-tactic__blocks'>
      {blocks.map(block => <div key={block.block} className={`oz-tactic__block ${block.saturated ? 'is-saturated' : ''} ${block.measured ? 'is-measured' : ''}`}
        title={`${block.from}–${block.to}: окон ${block.windows}${block.measured ? ', измерено' : ', оценка'}${block.saturated ? ', подъём ставки почти не добавляет кликов' : ''}`}>
        <div className='oz-tactic__bar'><span style={{ height: `${Math.max(4, block.elasticity / max * 100)}%` }} /></div>
        <strong>{number(block.elasticity)}</strong>
        <small>{block.from}–{block.to}</small>
      </div>)}
    </div>
    <p className='oz-auto__muted'>По группе в целом: {number(capacity.bid.elasticity)} {capacity.bid.measured ? '(измерено' : '(оценка'},
      окон с устойчивой ставкой: {capacity.bid.windows}, дней срезов: {capacity.snapshot_days}).</p>
    {prices.length > 0 && <>
      <h5>Цена и переход в корзину</h5>
      <ul className='oz-tactic__price-list'>{prices.map(([model, row]) => <li key={model}>
        <strong>{model}</strong>: {row.verdict}
        {row.ctr_elasticity != null && <small> · CTR к цене: {number(row.ctr_elasticity)}</small>}</li>)}</ul>
    </>}
  </div>;
}

// ── Сравнение периодов ──────────────────────────────────────────────────────

const COMPARED = [['orders', 'Заказы'], ['clicks', 'Клики РК'], ['carts', 'Корзины РК'], ['ad_spend', 'Расход РК, ₽'], ['profit', 'Прибыль, ₽']];
const BASE_OF = { last_week: 'prev_week', calibration: 'before_calibration', after_calibration: 'before_calibration' };

export function ComparisonCard({ comparison }) {
  if (!comparison?.length) return null;
  const byKey = Object.fromEntries(comparison.map(row => [row.key, row]));
  const delta = (row, key) => {
    const base = byKey[BASE_OF[row.key]];
    if (!base || base[key] == null || row[key] == null || !base[key]) return null;
    return row[key] / base[key] - 1;
  };
  return <div className='oz-tactic__comparison'>
    <h4>Периоды в среднем за день
      <Help text='Каждый новый период читается против предыдущих. Последние 7 дней — против недели до них. Калибровка и всё после неё — против тех же дней недели до калибровки: так будний и выходной день не смешиваются.' /></h4>
    <div className='oz-auto__table-scroll'><table>
      <thead><tr><th>Период</th><th>Дней</th>{COMPARED.map(([key, label]) => <th key={key}>{label}</th>)}</tr></thead>
      <tbody>{comparison.map(row => <tr key={row.key}>
        <td><strong>{row.label}</strong><small>{row.from} — {row.to}</small></td>
        <td>{row.days}</td>
        {COMPARED.map(([key]) => {
          const change = delta(row, key);
          return <td key={key}>{number(row[key])}
            {change != null && <small className={change >= 0 ? 'oz-tactic__up' : 'oz-tactic__down'}> {signed(change)}</small>}</td>;
        })}
      </tr>)}</tbody>
    </table></div>
  </div>;
}

// ── Пары и цены ─────────────────────────────────────────────────────────────

function LastChange({ change }) {
  if (!change) return <small>не менялась</small>;
  return <small>{stamp(change.at)} · {change.who}{change.before != null && ` · ${number(change.before)} → ${number(change.after)}`}</small>;
}

function ShelfCell({ row }) {
  const values = Object.values(row.shelf_now || {}).filter(value => value != null);
  if (!values.length) return <small>не замеряется</small>;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  return <>{number(Math.round(mean))} ₽
    {row.shelf_change != null && <small className={row.shelf_change >= 0 ? 'oz-tactic__up' : 'oz-tactic__down'}>
      {' '}{signed(row.shelf_change)} за 2 ч</small>}</>;
}

export function TacticPairs({ tactics, lastChanges }) {
  const [showAll, setShowAll] = useState(false);
  const bids = tactics?.bids || [];
  const prices = tactics?.prices || [];
  const visible = showAll ? bids : bids.slice(0, 10);
  return <>
    {bids.length > 0 && <div className='oz-auto__table-scroll'><table className='oz-tactic__pairs'>
      <thead><tr><th>РК / товар</th><th>Ставка</th><th>Кликов сегодня</th><th>Обычно в день</th><th>Отклик</th><th>Последнее изменение</th><th>Сейчас</th></tr></thead>
      <tbody>{visible.map(row => <tr key={row.key}>
        <td><strong>{row.campaign_name}</strong><small>{row.product_name} · SKU {row.sku_id}</small></td>
        <td>{number(row.bid)}{row.requested != null && <> → <strong>{number(row.requested)}</strong></>} ₽</td>
        <td>{number(row.clicks_today)}</td>
        <td>{number(row.usual_day)}</td>
        <td>{number(row.elasticity)}</td>
        <td><LastChange change={lastChanges?.[row.key]} /></td>
        <td><small>{row.reason}</small></td>
      </tr>)}</tbody>
    </table></div>}
    {bids.length > 10 && <button type='button' onClick={() => setShowAll(value => !value)}>
      {showAll ? 'Свернуть' : `Показать все пары (${bids.length})`}</button>}
    {prices.length > 0 && <>
      <h5>Цена по моделям
        <Help text='Цена держит переход клик → корзина модели: он почти не запаздывает, в отличие от заказов. CTR — второй сигнал. Отклик считается по витринной цене — той, что видит покупатель, вместе с соинвестом OZON; её замеряет «Мониторинг витрины». Если витрина уже сдвинулась сама, ценой продавца система не отвечает. Без замеров отклик считается по цене продавца.' /></h5>
      <div className='oz-auto__table-scroll'><table>
        <thead><tr><th>Модель</th><th>Витрина</th><th>Клик → корзина сегодня</th><th>Обычно</th><th>Отклонение</th><th>CTR к обычному</th><th>Последнее изменение</th><th>Сейчас</th></tr></thead>
        <tbody>{prices.map(row => <tr key={row.model}>
          <td><strong>{row.model}</strong><small>{row.sku_ids.length} SKU · кликов сегодня {number(row.clicks_today)}</small></td>
          <td><ShelfCell row={row} /></td>
          <td>{row.conversion_today == null ? '—' : `${(row.conversion_today * 100).toFixed(1)}%`}</td>
          <td>{row.conversion_usual == null ? '—' : `${(row.conversion_usual * 100).toFixed(1)}%`}</td>
          <td>{signed(row.deviation)}</td>
          <td>{signed(row.ctr_deviation)}</td>
          <td><LastChange change={lastChanges?.[`PRICE:${row.sku_ids[0]}`]} /></td>
          <td><small>{row.move != null && <strong>цена {signed(row.move)}. </strong>}{row.reason}</small></td>
        </tr>)}</tbody>
      </table></div>
    </>}
  </>;
}

// ── Выбор цены и ставок (только план) ───────────────────────────────────────

const CHOICE_STATUS = {
  CHANGE: ['Сменить пару', 'is-ok'], HOLD: ['Держать текущую', 'is-waiting'], PROTECTIVE: ['Защита рентабельности', 'is-action'],
  NO_FEASIBLE_ACTION: ['Нет допустимого действия', 'is-action'], INSUFFICIENT_DATA: ['Мало данных', 'is-waiting'],
  ERROR: ['Ошибка расчёта', 'is-action'],
};

function Forecast({ value, adverse, money, pct }) {
  const fmt = v => v == null ? '—' : pct ? `${(v * 100).toFixed(1)}%` : `${number(v)}${money ? ' ₽' : ''}`;
  return <>{fmt(value)}{adverse != null && adverse !== value && <small>хуже: {fmt(adverse)}</small>}</>;
}

export function ChoiceCard({ choice }) {
  const [showVectors, setShowVectors] = useState(false);
  if (!choice) return null;
  const [label, cls] = CHOICE_STATUS[choice.status] || [choice.status, ''];
  const candidates = choice.candidates || [];
  const chosen = choice.chosen;
  const isChosen = cand => chosen && cand.bid_effective === chosen.bid_effective && cand.price_effective === chosen.price_effective && !cand.rejected?.length;
  const moved = rows => (rows || []).filter(row => row.new !== row.now);
  return <div className='oz-auto__planner'>
    <h4>Выбор цены и ставок — только план
      <Help text='До девяти вариантов: цена (как сейчас, шаг вниз, шаг вверх) × ставки всех размещений (держать, шаг вниз, шаг вверх). Каждый вариант считается целиком на 7 дней нового спроса с выкупами: заказы, расход, прибыль, рентабельность и ДРР — в обычном и неблагоприятном сценарии. Сначала отбрасывается всё, что нарушает права, курс ИИ, коридоры цены и ставок и минимальную рентабельность; затем выбирается лучший по цели группы. Если выигрыш не больше ошибки — остаётся текущая пара. В OZON ничего не отправляется.' /></h4>
    <p><span className={`oz-tactic__status ${cls}`}>{label}</span> {choice.reason}</p>
    {choice.current && <div className='oz-tactic__kpis'>
      <span><small>Цель</small><strong>{choice.metric_label}</strong></span>
      <span><small>Сейчас за 7 дн.: заказы</small><strong>{number(choice.current.base.orders)}</strong></span>
      <span><small>Прибыль</small><strong>{number(choice.current.base.profit)} ₽</strong></span>
      <span><small>Рентабельность</small><strong>{choice.current.base.margin_pct == null ? '—' : `${choice.current.base.margin_pct}%`}</strong></span>
      <span><small>ДРР</small><strong>{choice.current.base.drr == null ? '—' : `${(choice.current.base.drr * 100).toFixed(1)}%`}</strong></span>
    </div>}
    {candidates.length > 0 && <div className='oz-auto__table-scroll'><table>
      <thead><tr><th>Цена</th><th>Ставки</th><th>Заказы</th><th>Расход</th><th>Прибыль</th><th>Рентаб.</th><th>ДРР</th><th>Итог</th></tr></thead>
      <tbody>{candidates.map((cand, index) => {
        const current = !cand.bid_effective && !cand.price_effective && !cand.rejected?.length;
        return <tr key={index} className={isChosen(cand) ? 'is-chosen' : ''}>
          <td>{signed(cand.price_effective)}</td>
          <td>{signed(cand.bid_effective)}</td>
          <td><Forecast value={cand.base.orders} adverse={cand.adverse.orders} /></td>
          <td><Forecast value={cand.base.ads} adverse={cand.adverse.ads} money /></td>
          <td><Forecast value={cand.base.profit} adverse={cand.adverse.profit} money /></td>
          <td>{cand.base.margin_pct == null ? '—' : `${cand.base.margin_pct}%`}<small>хуже: {cand.adverse.margin_pct ?? '—'}%</small></td>
          <td><Forecast value={cand.base.drr} adverse={cand.adverse.drr} pct /></td>
          <td>{cand.rejected?.length ? <small>отклонён: {cand.rejected.join('; ')}</small>
            : isChosen(cand) ? <strong>выбран</strong> : current ? <small>текущая пара</small> : <small>допустим</small>}
            {!cand.autonomous_ok && cand.bid_effective > 0 && !cand.rejected?.length && <small>не для автоисполнения: отклик расхода не измерен</small>}</td>
        </tr>;
      })}</tbody>
    </table></div>}
    {chosen && (moved(chosen.bids).length > 0 || moved(chosen.prices).length > 0) && <>
      <button type='button' onClick={() => setShowVectors(value => !value)}>
        {showVectors ? 'Скрыть значения' : `Значения выбранной пары: ставок ${moved(chosen.bids).length}, цен ${moved(chosen.prices).length}`}</button>
      {showVectors && <ul className='oz-tactic__price-list'>
        {moved(chosen.bids).map(row => <li key={row.key}>Ставка {row.label || row.key}: {number(row.now)} → <strong>{number(row.new)}</strong> ₽</li>)}
        {moved(chosen.prices).map(row => <li key={row.sku}>Цена SKU {row.sku}: {number(row.now)} → <strong>{number(row.new)}</strong> ₽</li>)}
      </ul>}
    </>}
    {(choice.quality || []).length > 0 && <>
      <h5>Качество данных</h5>
      <ul className='oz-tactic__price-list'>{choice.quality.map(note => <li key={note}>{note}</li>)}</ul>
    </>}
    <p className='oz-auto__muted'>{choice.applied || 'только план'} · пересчёт каждые 30 минут, вместе с тактикой.</p>
  </div>;
}

// ── Магазин: реклама и соинвест ─────────────────────────────────────────────

const SHOP_DECISION = {
  UP: ['Наращивать рекламу магазина', 'is-ok'], DOWN: ['Снижать рекламу магазина', 'is-action'],
  HOLD: ['Держать уровень', 'is-waiting'], EXPLORE: ['Разведка', 'is-optional'], NONE: ['Точка не найдена', 'is-waiting'],
};
const LINK_LABEL = { LINK: 'связь найдена', NO_LINK: 'связи не видно', INSUFFICIENT_DATA: 'мало данных' };
const pp = value => value == null ? '—' : `${value > 0 ? '+' : ''}${Number(value).toFixed(2)} п.п.`;

function ContestTable({ contest, unit }) {
  const rows = contest?.table || [];
  if (!rows.length) return null;
  return <div className='oz-auto__table-scroll'><table>
    <thead><tr><th>Гипотеза</th><th>Точнее «нет связи»</th><th>Недель лучше</th><th>+10% рекламы →</th></tr></thead>
    <tbody>{rows.map(row => <tr key={row.key} className={row.key === contest.winner?.key ? 'is-chosen' : ''}>
      <td>{row.label}</td>
      <td>{row.skill == null ? '—' : `${(row.skill * 100).toFixed(0)}%`}</td>
      <td>{row.weeks_better} из {row.weeks}</td>
      <td>{row.lift == null ? '—' : unit(row.lift)}{row.lift_se != null && <small>± {unit(2 * row.lift_se)}</small>}</td>
    </tr>)}</tbody>
  </table></div>;
}

const REVIEW_SIDE = { up: 'Подъём ставки', down: 'Снижение ставки' };
const REVIEW_SOURCE = {
  'группа': 'по самой группе',
  'группа и другие группы': 'по группе, уточнено другими группами',
  'другие группы': 'по другим группам (у этой мало шагов)',
};

// «+10% ставки → +X% кликов»: отклик 3.0 значит, что +1% ставки давал +3% кликов.
// У снижения отклик считается «по ходу шага»: положительный — трафик ушёл вниз вслед за ставкой.
const reviewLine = (used, side) => {
  if (!used) return 'мало шагов для оценки';
  const sign = side === 'down' ? '−' : '+';
  const per10 = Math.abs(used.elasticity * 10);
  const wrong = used.elasticity < 0;
  const half = used.se * 2 * 10;
  const verdict = used.significant ? '' : ' — от нуля не отличить';
  return `${sign}10% ставки → ${wrong ? (side === 'down' ? '+' : '−') : sign}${per10.toFixed(0)}% кликов (±${half.toFixed(0)})${verdict}`;
};

export function ReviewCard({ review }) {
  if (!review) return null;
  if (review.empty) return <div className='oz-auto__planner'>
    <h4>Как заканчивались шаги ставки</h4>
    <p className='oz-auto__muted'>У этой группы пока нет получасовых снимков для разбора.</p>
  </div>;
  const placebo = review.placebo?.['1'];
  const marginal = review.marginal;
  return <div className='oz-auto__planner'>
    <h4>Как заканчивались шаги ставки — разбор задним числом
      <Help text='Каждое движение ставки сравнивается с теми же часами без движения: та же группа, тот же час суток, та же ситуация до шага (трафик против обычного), тот же уровень ставки. Окна, где двигалась витрина или цена, не берутся: они меняют спрос сами. Отклик — на сколько процентов изменился трафик на 1% ставки. Если у группы мало своих шагов, берётся оценка других групп. Это короткий отклик (1–2 часа), а не вся добавка за день.' /></h4>
    <p className='oz-auto__muted'>За {review.days} дн. · обновлено {review.as_of}</p>
    <div className='oz-auto__table-scroll'><table>
      <thead><tr><th>Шаг</th><th>Через час</th><th>Через два часа</th><th>Чья оценка</th></tr></thead>
      <tbody>{['up', 'down'].map(side => {
        const one = review.bid?.[side]?.['1'];
        const two = review.bid?.[side]?.['2'];
        return <tr key={side}>
          <td>{REVIEW_SIDE[side]}<br /><small>своих шагов: {one?.own?.n ?? 0}</small></td>
          <td>{reviewLine(one?.used, side)}</td>
          <td>{reviewLine(two?.used, side)}</td>
          <td><small>{REVIEW_SOURCE[one?.used?.source] || '—'}</small></td>
        </tr>;
      })}</tbody>
    </table></div>
    {placebo && <p className='oz-auto__muted'>Для сравнения: в моментах, где тактика хотела поднять ставку, но ничего не отправила, «отклик» получается {placebo.elasticity > 0 ? '+' : ''}{(placebo.elasticity * 10).toFixed(0)}% на +10% — это вклад самого момента, а не ставки.</p>}
    {marginal && <p>Подъёмы в этом кабинете за два часа добавили {number(marginal.clicks)} кликов и {number(marginal.carts)} корзин
      за {number(marginal.spent)} ₽{marginal.rub_per_extra_cart != null && <>; дополнительная корзина обошлась в {number(marginal.rub_per_extra_cart)} ₽</>}.</p>}
  </div>;
}

export function ShopCard({ detail }) {
  const [open, setOpen] = useState(false);
  if (!detail) return null;
  const [label, cls] = SHOP_DECISION[detail.decision] || [detail.decision, ''];
  const coin = detail.coinvest || {};
  const lever = coin.lever_winner;
  const point = detail.point || {};
  const ordersLift = value => `${((Math.exp(value) - 1) * 100).toFixed(1)}% заказов`;
  return <div className='oz-auto__planner'>
    <h4>Магазин: реклама и соинвест
      <Help text='Соинвест OZON зависит от рекламы всего магазина, а не группы, и тянется во времени. Каждый день по всем кабинетам проверяются все гипотезы: что считать рекламой (расход, ДРР, расход на заказ, виды рекламы), с какой задержкой и в какой форме. Судья — прогноз недель, которых модель не видела, против «нет связи». Отдельно меряется, как заказы магазина отвечают на рекламу: отсюда отдача последнего рубля и точка, к которой идёт реклама магазина. Граница — только минимум рентабельности каждой группы.' /></h4>
    <p><span className={`oz-tactic__status ${cls}`}>{label}</span> {detail.reason}</p>
    {point.status === 'OK' && <div className='oz-tactic__kpis'>
      <span><small>Отдача последнего рубля</small><strong>{point.return_per_rouble} ₽</strong><small>{point.low}…{point.high}</small></span>
      <span><small>Реклама магазина в день</small><strong>{number(point.ads_per_day)} ₽</strong></span>
      <span><small>Заказов в день</small><strong>{number(point.orders_per_day)}</strong></span>
      <span><small>Маржа заказа до рекламы</small><strong>{number(point.value_per_order)} ₽</strong></span>
    </div>}
    {detail.guard?.note && <p className='oz-auto__hint is-action'>{detail.guard.note}</p>}
    <p><strong>Соинвест:</strong> {LINK_LABEL[coin.status] || coin.status}
      {coin.winner && <> — лучше всего объясняет «{coin.winner.label}»</>}
      {lever && <>; по рекламе — «{lever.label}»: +10% рекламы → {pp(lever.lift)} соинвеста</>}
      <small className='oz-auto__muted'> · данные {detail.coinvest_days?.from} — {detail.coinvest_days?.to}, дней в оценке {coin.days}</small></p>
    {(detail.regime?.notes || []).map(note => <p key={note} className='oz-auto__hint is-action'>Перелом: {note}</p>)}
    <button type='button' onClick={() => setOpen(value => !value)}>{open ? 'Свернуть разбор' : 'Весь разбор'}</button>
    {open && <>
      <h5>Гипотезы соинвеста</h5>
      <ContestTable contest={coin} unit={pp} />
      <h5>Заказы магазина и реклама</h5>
      <p className='oz-auto__muted'>{LINK_LABEL[detail.orders?.status] || detail.orders?.status}
        {point.by && <> · отдача считается по «{point.by}»</>}</p>
      <ContestTable contest={detail.orders} unit={ordersLift} />
      {detail.months?.length > 0 && <>
        <h5>По месяцам</h5>
        <div className='oz-auto__table-scroll'><table>
          <thead><tr><th>Месяц</th><th>Связь</th><th>Лидер</th><th>+10% рекламы →</th></tr></thead>
          <tbody>{detail.months.map(m => <tr key={m.month}>
            <td>{m.month}</td><td>{LINK_LABEL[m.status] || m.status}</td><td><small>{m.winner || '—'}</small></td>
            <td>{pp(m.lift)}{m.lift_se != null && <small>± {pp(2 * m.lift_se)}</small>}</td></tr>)}</tbody>
        </table></div>
      </>}
      {detail.events && <>
        <h5>Ступени рекламы магазина ±15% и соинвест после них <small>(за вычетом фона OZON)</small></h5>
        <ul className='oz-tactic__price-list'>
          {['up', 'down'].map(side => <li key={side}>{side === 'up' ? 'Рост' : 'Снижение'} ({detail.events.count?.[side] || 0}):{' '}
            {Object.entries(detail.events.path?.[side] || {}).map(([day, value]) => `день ${day}: ${pp(value)}`).join(' · ') || '—'}</li>)}
        </ul>
      </>}
      <ul className='oz-tactic__price-list'>
        {detail.direction?.note && <li>Направление (соинвест): {detail.direction.note}</li>}
        {detail.direction_orders?.reverse_skill != null && <li>Направление (заказы): обратная дорога точнее «нет связи» на {(detail.direction_orders.reverse_skill * 100).toFixed(0)}%</li>}
        {detail.level?.verdict && <li>Уровень эффекта: {detail.level.verdict}</li>}
        {detail.shocks && <li>Скачки соинвеста по рынку: {detail.shocks.count}</li>}
        {(detail.forecasts || []).map(f => <li key={f.for}>Прогноз соинвеста на {f.for}: {f.value}%</li>)}
        {detail.forecast_check?.checked > 0 && <li>Проверено прогнозов: {detail.forecast_check.checked}, средняя ошибка {detail.forecast_check.mae_pp} п.п.</li>}
        {detail.missing?.length > 0 && <li>Нет данных: {detail.missing.join(', ')}</li>}
      </ul>
    </>}
  </div>;
}

// ── Остатки: лестница ───────────────────────────────────────────────────────

const ZONE_CLASS = { FREE: 'is-ok', BRAKE: 'is-waiting', CRITICAL: 'is-action', ZERO: 'is-action' };

export function StockCard({ tactics }) {
  const rows = (tactics?.stock || []).filter(row => row.zone !== 'FREE' || row.stage_label
    || row.removed_from?.length || row.paused?.length);
  const all = tactics?.stock || [];
  if (!all.length) return null;
  const names = Object.fromEntries((tactics.bids || []).map(row => [row.sku_id, row.product_name]));
  return <div className='oz-tactic__capacity'>
    <h4>Остатки
      <Help text='Работает всегда. Скорость продаж — с затуханием: последние дни весят больше, а сегодняшний темп учитывается, если он вдвое выше обычного. Остаток (с товаром в пути на склад OZON) делится на горизонт — период пополнения, если задан; иначе типичный интервал между поставками по истории склада; иначе 14 дней. Если остаток не выдерживает скорость: сначала ставка вниз до минимума OZON, затем цена вверх, а если цена на максимуме — товар убирается из РК (РК с одним товаром — на паузу). Остаток 0: РК с одним товаром — пауза, в общей РК — минимальная ставка. Возврат — когда средний остаток за 3 дня покрывает неделю продаж.' /></h4>
    {!rows.length ? <p className='oz-auto__muted'>У всех {all.length} товаров запаса достаточно — ограничений нет.</p>
      : <div className='oz-auto__table-scroll'><table>
        <thead><tr><th>Товар</th><th>Остаток</th><th>Продаётся в день</th><th>Запас, дн.</th><th>Выдержит в день</th><th>Состояние</th><th>Ступень</th></tr></thead>
        <tbody>{rows.map(row => <tr key={row.sku_id}>
          <td><strong>{names[row.sku_id] || `SKU ${row.sku_id}`}</strong><small>SKU {row.sku_id}</small></td>
          <td>{number(row.stock)}{row.transit > 0 && <small>в т.ч. в пути на склад: {number(row.transit)}</small>}</td>
          <td>{number(row.rate)}<small>{row.rate_source}</small></td>
          <td>{number(row.cover_days)}<small>горизонт {number(row.horizon_days)} дн.{row.horizon_source ? ` — ${row.horizon_source}` : ''}</small></td>
          <td>{number(row.sustainable_rate)}</td>
          <td><span className={`oz-tactic__status ${ZONE_CLASS[row.zone] || ''}`}>{row.zone_label}</span><small>{row.reason}</small></td>
          <td>{row.stage_label || '—'}
            {row.removed_from?.length > 0 && <small>убран из РК: {row.removed_from.length}</small>}
            {row.paused?.length > 0 && <small>РК на паузе: {row.paused.length}</small>}</td>
        </tr>)}</tbody>
      </table></div>}
  </div>;
}

// ── Вкладка целиком ─────────────────────────────────────────────────────────

const ORDER_VALUES = {
  plan_level: { MUCH_LOWER: 'сильно ниже', LOWER: 'ниже', KEEP: 'как есть', HIGHER: 'выше', MUCH_HIGHER: 'сильно выше' },
  direction: { BOTH: 'в обе стороны', UP_ONLY: 'только вверх', DOWN_ONLY: 'только вниз', FREEZE: 'заморожена' },
  aggressiveness: { CAUTIOUS: 'осторожно', NORMAL: 'обычно', BOLD: 'смело' },
};

function AiCourseCard({ order, conflict }) {
  if (!order) return null;
  return <div className='oz-auto__planner'>
    <h4>Курс от ИИ
      <Help text='Раз в сутки ИИ выбирает курс тактики, который ведёт к цели вашей стратегии, и поправляет его в течение дня при резких изменениях. Тактика каждые полчаса шагает ставкой и ценой в рамках этого курса. Ваши ограничители (лимиты ставки и цены, минимальная рентабельность, остатки) курс не отменяет.' /></h4>
    <p><strong>{order.course_label}</strong>
      {order.given_at && <small className='oz-auto__muted'> · с {fmtDateTime(order.given_at)}</small>}</p>
    <p className='oz-auto__muted'>
      План трафика: {ORDER_VALUES.plan_level[order.plan_level] || order.plan_level} ·
      ставка: {ORDER_VALUES.direction[order.bid] || order.bid} ·
      цена: {ORDER_VALUES.direction[order.price] || order.price} ·
      шаги: {ORDER_VALUES.aggressiveness[order.aggressiveness] || order.aggressiveness}
    </p>
    {order.recommendation_label && <div className='oz-auto__notice'>
      <strong>Рекомендация</strong>
      <p>{order.recommendation_label}. Ставкой и ценой здесь уже сделано всё, что позволяет стратегия.
        Когда поменяете карточку — отметьте это на вкладке «События».</p></div>}
    {conflict && <div className='oz-auto__notice'><strong>Курс упёрся в ограничители</strong><p>{conflict}</p></div>}
  </div>;
}

const PAYBACK_VERDICTS = {
  pays: ['Окупилось', 'ok'], loses: ['Не окупается — возврат', 'bad'], wait: ['Мало данных — не поднимаем', 'warn'],
  unknown: ['Нет себестоимости — не поднимаем', 'warn'], surge_cut: ['Всплеск без заказов — снижаем', 'bad'],
  surge_hold: ['Всплеск — удерживаем', 'warn'], hold: ['Удерживаем после неудачи', 'warn'],
  rare_min: ['Редкие заказы — минимальная ставка', 'warn'], rare_off: ['Реклама выключена', 'bad'],
  ok: ['Ставка на проверенном уровне', 'ok'],
};

// Окупаемость подъёма ставки по парам: каждый подъём выше проверенного уровня — проба, расход против спроса
// (корзины × доля заказов артикула: отложенный спрос), а не против уже пришедших заказов.
export function PaybackCard({ payback }) {
  const rows = Object.entries(payback || {}).filter(([, v]) => v && v.verdict !== 'no_data');
  if (!rows.length) return null;
  const attention = rows.filter(([, v]) => v.verdict !== 'ok');
  return <div className='oz-auto__planner'>
    <div className='oz-tactic__head'>
      <h4>Окупаемость ставок<Help text='Ставка выше проверенного уровня — проба: лишний расход сравнивается с лишним спросом (корзины × доля, в которую корзины артикула становятся заказами, то есть с учётом отложенного спроса). Окупилась — уровень растёт. Не окупилась — ставка возвращается и держится. Мало данных — выше не поднимаем.' /></h4>
      <span className='oz-auto__muted'>на уровне: {rows.length - attention.length} · требуют внимания: {attention.length}</span>
    </div>
    {attention.length === 0
      ? <p className='oz-auto__muted'>Все ставки группы на проверенном уровне.</p>
      : <div className='oz-auto__table-scroll'><table>
        <thead><tr><th>Артикул</th><th>Вердикт</th><th>Проверенный уровень</th><th>Пояснение</th></tr></thead>
        <tbody>{attention.map(([key, v]) => {
          const [label, tone] = PAYBACK_VERDICTS[v.verdict] || [v.verdict, 'warn'];
          return <tr key={key}>
            <td>{key.split(':').pop()}</td>
            <td className={`is-${tone}`}>{label}</td>
            <td>{v.verified != null ? `${Number(v.verified).toLocaleString('ru-RU')} ₽` : '—'}</td>
            <td>{v.note}{v.basis === 'orders' && ' (по пришедшим заказам: истории отложенного спроса нет)'}</td>
          </tr>;
        })}</tbody>
      </table></div>}
  </div>;
}

const lastTotalDrr = series => {
  const row = [...(series || [])].reverse().find(item => item.drr_total != null);
  return row ? { date: row.date, value: row.drr_total } : null;
};

export function TacticTab({ data, item, base, request, busy, onCalibration }) {
  const [date, setDate] = useState('');
  const [day, setDay] = useState(data.tactic_day);
  const [dayError, setDayError] = useState('');
  useEffect(() => { if (!date) setDay(data.tactic_day); }, [data.tactic_day, date]);
  const loadDay = useCallback(async value => {
    setDate(value); setDayError('');
    if (!value) { setDay(data.tactic_day); return; }
    try { setDay(await request(`${base}/tactic-day?date=${value}`)); }
    catch (err) { setDayError(failure(err)); }
  }, [base, request, data.tactic_day]);
  const days = [...Array(7)].map((_, index) => {
    const value = new Date(Date.now() - index * 86400000);
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' }).format(value);
  });
  const tactics = data.tactics;
  return <div className='oz-tactic'>
    <AiCourseCard order={tactics?.order} conflict={tactics?.order_conflict} />
    <PaybackCard payback={tactics?.payback} />
    <CalibrationCard calibration={tactics?.calibration} capacity={tactics?.capacity} busy={busy}
      mode={item.execution_mode} status={item.status}
      onStart={() => onCalibration('POST')} onStop={() => onCalibration('DELETE')} />
    <div className='oz-auto__planner'>
      <div className='oz-tactic__head'>
        <h4>День группы по получасам
          <Help text='Синяя линия — клики группы с начала дня по срезам каждые полчаса. Серая — сколько обычно набирается к этому часу, зелёная — цель стратегии. Ниже — средняя ставка пар и цена. Треугольники — поправки: вверх или вниз, цвет — кто их сделал.' /></h4>
        <select value={date} onChange={e => loadDay(e.target.value)} aria-label='День'>
          {days.map((value, index) => <option key={value} value={index === 0 ? '' : value}>
            {index === 0 ? 'Сегодня' : index === 1 ? 'Вчера' : value}</option>)}
        </select>
      </div>
      {dayError && <p role='alert' className='bidder__error'>{dayError}</p>}
      <TacticDayChart day={day} sources={data.tactic_day?.sources} totalDrr={lastTotalDrr(data.series)} />
    </div>
    <GroupDynamics base={base} request={request} />
    <div className='oz-auto__planner'>
      <h4>Тактика — поправки каждые 30 минут
        <Help text='Одно решение для всей группы. Недостающие или лишние клики переводятся в шаг ставки через измеренный отклик этого часа — половина нужного шага за раз, чтобы не раскачивать. Пары, которые лучше отвечают на ставку, двигаются сильнее. В коридоре ±7% ничего не трогается; после поправки система ждёт час, пока она проявится. Границы — минимум OZON, максимальная ставка, коридор цены, минимальная рентабельность и остаток.' /></h4>
      <GroupDecision tactics={tactics} mode={item.execution_mode} />
      <TacticPairs tactics={tactics} lastChanges={data.last_changes} />
    </div>
    <ShopCard detail={tactics?.shop_detail} />
    <ReviewCard review={tactics?.review} />
    <ChoiceCard choice={tactics?.choice} />
    <StockCard tactics={tactics} />
    <CapacityCard capacity={tactics?.capacity} />
    <ComparisonCard comparison={data.comparison} />
  </div>;
}

// ── Журнал всех изменений ───────────────────────────────────────────────────

const PAGE = 100;

export function JournalCard({ base, request, actions, renderAction, reloadKey }) {
  const [filters, setFilters] = useState({ lever: '', source: '', status: '' });
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);

  const fetchPage = useCallback(async (offset, current) => {
    setLoading(true); setError('');
    const params = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
    Object.entries(current).forEach(([key, value]) => { if (value) params.set(key, value); });
    try {
      const response = await request(`${base}/journal?${params.toString()}`);
      setMeta(response);
      setRows(previous => offset ? [...previous, ...response.items] : response.items);
    } catch (err) { setError(failure(err)); }
    finally { setLoading(false); }
  }, [base, request]);

  useEffect(() => { fetchPage(0, filters); }, [fetchPage, filters, reloadKey]);

  const set = (key, value) => setFilters(current => ({ ...current, [key]: value }));
  const actionById = Object.fromEntries((actions || []).map(action => [action.id, action]));
  return <div className='oz-tactic__journal'>
    <div className='oz-tactic__filters'>
      <label>Рычаг <select value={filters.lever} onChange={e => set('lever', e.target.value)}>
        <option value=''>Все</option><option value='BID'>Ставка</option><option value='PRICE'>Цена</option><option value='GOAL'>Цель тактики</option>
        <option value='RK_PAUSE'>Пауза РК</option><option value='RK_ON'>Включение РК</option>
        <option value='RK_REMOVE'>Удаление из РК</option><option value='RK_RETURN'>Возврат в РК</option>
      </select></label>
      <label>Кто <select value={filters.source} onChange={e => set('source', e.target.value)}>
        <option value=''>Все</option>
        {Object.entries(meta?.sources || { TACTIC: 'Тактика', CALIBRATION: 'Калибровка', STOCK: 'Остатки', STRATEGY: 'Стратегия', MANUAL: 'Вручную', HYPOTHESIS: 'Проверка гипотезы' })
          .map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select></label>
      <label>Статус <select value={filters.status} onChange={e => set('status', e.target.value)}>
        <option value=''>Все</option>
        {Object.entries(meta?.statuses || { SENT: 'отправлено', WOULD: 'сделал бы', SKIPPED: 'пропущено', ERROR: 'ошибка' })
          .map(([key, label]) => <option key={key} value={key}>{label}</option>)}
      </select></label>
      <span className='oz-auto__muted'>{meta ? `Записей: ${meta.total}` : ''}</span>
    </div>
    {error && <p role='alert' className='bidder__error'>{error}</p>}
    {!loading && !rows.length && <p>Изменений с такими условиями нет. Тактика пишет сюда каждую поправку — отправленную,
      несостоявшуюся и ту, что сделала бы в режиме с подтверждением.</p>}
    {rows.length > 0 && <div className='oz-auto__table-scroll'><table className='oz-tactic__journal-table'>
      <thead><tr><th>Когда</th><th>Кто</th><th>Что</th><th>Где</th><th>Было → стало</th><th>Статус</th><th>Почему</th></tr></thead>
      <tbody>{rows.map(row => {
        const action = row.action_id ? actionById[row.action_id] : null;
        return [
          <tr key={row.id} className={action ? 'is-clickable' : ''} onClick={() => action && setOpen(open === row.id ? null : row.id)}>
            <td>{stamp(row.at)}</td>
            <td><span className={`oz-tactic__badge ${SOURCE_CLASS[row.source] || ''}`}>{row.source_label}</span></td>
            <td>{row.lever_label}</td>
            <td>{row.campaign_name && <strong>{row.campaign_name}</strong>}<small>{row.product_name || row.target_key}{row.sku_id ? ` · SKU ${row.sku_id}` : ''}</small></td>
            <td>{row.lever?.startsWith('RK_') ? (row.before != null ? `ставка ${number(row.before)} ₽` : '—')
              : row.lever === 'GOAL'
              ? `${number(row.before)}% → ${number(row.after)}%`
              : `${number(row.before)} → ${number(row.after)}${row.lever === 'BID' || row.lever === 'PRICE' ? ' ₽' : ''}`}</td>
            <td><span className={`oz-tactic__status ${STATUS_CLASS[row.status] || ''}`}>{row.status_label}</span></td>
            <td><small>{row.reason}</small>{action && <small className='oz-tactic__more'>{open === row.id ? 'Свернуть' : 'Подробнее'}</small>}</td>
          </tr>,
          open === row.id && action && <tr key={`${row.id}-details`} className='oz-tactic__details'>
            <td colSpan='7'>{renderAction(action)}</td></tr>,
        ];
      })}</tbody>
    </table></div>}
    {meta && rows.length < meta.total && <button type='button' disabled={loading}
      onClick={() => fetchPage(rows.length, filters)}>{loading ? 'Загрузка…' : `Показать ещё (${meta.total - rows.length})`}</button>}
  </div>;
}

